import React, { useRef } from 'react';
import WrappedLink from './WrappedLink';
import PropTypes from 'prop-types';
import { BodyText, Button, HeadingText } from '@paypalcorp/pp-react';
import Favorite from './Favorite';
import { get } from 'lodash';
import classNames from 'classnames';
import {
  PARTNERS,
  EXTERNAL_PARTNERS,
  BIG_COMMERCE
} from '../../../lib/constants';
import { parseData } from '../../../lib/helpers';
import { useFptiImpression } from '../../../lib/hooks';

const getCurrentCategory = (flatPSData, categoryKey) =>
  get(flatPSData, ['categories', categoryKey]) || {};
const getCurrentSection = (flatPSData, sectionKey) =>
  get(flatPSData, ['sections', sectionKey]) || {};
const getCurrentFPTIPage = (insideSection, fpti, currentCategory) =>
  insideSection ? fpti : currentCategory.categoryFPTI;
const getPageLink = (
  pageLink,
  crossSellType,
  currentFPTIPage,
  sectionFptiName
) => pageLink || (!crossSellType && currentFPTIPage) || sectionFptiName;
const getImgUrl = appIcon => parseData(appIcon)[0] || {};
const getUrlColored = imgUrl => (imgUrl.urlColored ? imgUrl.urlColored : '');
const isPartnerOrMarketplace = appKey =>
  EXTERNAL_PARTNERS.includes(appKey) ||
  PARTNERS.includes(appKey) ||
  appKey === BIG_COMMERCE;

const getFptiLink = (favoriteLink, appFPTI) =>
  favoriteLink ? favoriteLink : appFPTI;

const CategoryToolTile = props => {
  const {
    fptiImpression,
    fpti = '',
    tool: {
      appIcon = '',
      appBody = '',
      appTitle = '',
      appKey = '',
      appFPTI = '',
      location: to = '',
      categoryKey = '',
      ariaLabel = '',
      PSClickUrl = '',
      rank = '',
      priorityScore = '',
      sectionKey = '',
      shortDescription = ''
    } = {},
    toolTileProps: {
      toolServFavorites = [],
      app,
      flatPSData = {},
      experimentation
    } = {},
    insideSection = false,
    pageLink = '',
    crossSellType = '',
    staffPickType = '',
    intentPickType = '',
    intentRank = 0,
    handleClickTool,
    path,
    hideFavorites,
    largeTile,
    structuredPSData = {},
    isCollections = false,
    collection = {},
    section = {},
    lazyLoadImage = true
  } = props;
  const { fptiLinkName = '' } = collection;
  const { sectionFptiName = '' } = section;
  const isNewLayoutEligible = get(flatPSData, 'isNewLayoutEligible');
  const currentCategory = getCurrentCategory(flatPSData);
  const currentSection = getCurrentSection(flatPSData, sectionKey);
  const currentFPTIPage = getCurrentFPTIPage(
    insideSection,
    fpti,
    currentCategory
  );
  const isColoredIconsEligible = get(experimentation, 'coloredIconsExp', false);
  const content = get(structuredPSData, 'content', {});
  const fptiObj = {
    prod: appFPTI,
    pageLink: getPageLink(
      pageLink,
      crossSellType,
      currentFPTIPage,
      sectionFptiName
    ),
    type: sectionFptiName ? fptiLinkName : '',
    categoryRank: currentCategory.rank,
    sectionRank: currentSection.rank,
    appKey,
    rank,
    priorityScore
  };

  if (crossSellType) {
    Object.assign(fptiObj, { type: crossSellType });
  }
  if (staffPickType) {
    Object.assign(fptiObj, { type: staffPickType });
  }
  if (intentPickType) {
    Object.assign(fptiObj, { type: intentPickType, intentRank });
  }
  const ref = useRef();
  useFptiImpression(ref, () => {
    fptiImpression(fptiObj);
  });

  const handleFavoriteClick = () => {
    const {
      toolTileProps: {
        dispatch,
        toggleToolFavorite,
        tools: toolServTools
      } = {},
      tool: { appKey = '' } = {}
    } = props;

    const MTSTool = toolServTools[appKey];
    // if it can be favorited by toolserv do so
    dispatch(toggleToolFavorite(MTSTool));
  };

  const imgUrl = getImgUrl(appIcon);
  const imgBackgroundColor = imgUrl.backgroundColor;
  const coloredImgUrl = isColoredIconsEligible && getUrlColored(imgUrl);
  const partnerOrMarketplace = isPartnerOrMarketplace(appKey);

  const clickToolHandler = favoriteLink => {
    const currentCategory = getCurrentCategory(flatPSData, categoryKey);
    const currentSection = getCurrentSection(flatPSData, sectionKey);
    return handleClickTool({
      fptiLink: getFptiLink(favoriteLink, appFPTI),
      prod: appFPTI,
      PSUrl: PSClickUrl,
      categoryKey,
      categoryRank: currentCategory.rank,
      sectionRank: currentSection.rank,
      intentRank,
      appKey,
      rank,
      priorityScore
    });
  };

  const tickerHelper = (appTitle, className, maxLength) =>
    `${className} ${
      appTitle.replace(/\s+/g, '').length > maxLength ? 'ticker' : ''
    }`;

  const lazyLoadImageBackground = lazyLoadImage
    ? {}
    : {
        backgroundImage: `url(${coloredImgUrl || imgUrl.url})`
      };

  if (largeTile) {
    return (
      <WrappedLink
        className={classNames('undo-link-styles', 'add-focus-visible', {
          'tile-link-wrapper': !isNewLayoutEligible
        })}
        onClick={clickToolHandler()}
        to={`${path}/${to}`}
        ref={ref}
      >
        <div className="large-tool-tile">
          <div className="large-tool-inner">
            <div
              className={
                !imgBackgroundColor ||
                imgBackgroundColor.toLowerCase() === '#ffffff'
                  ? 'large-tool-media media-border'
                  : 'large-tool-media'
              }
              style={{ backgroundColor: imgBackgroundColor || '#ffffff' }}
            >
              <div className="large-tool-wrapper">
                <div
                  className={classNames(
                    { lazyload: lazyLoadImage },
                    'large-tool-image'
                  )}
                  style={{
                    background: `center center no-repeat`,
                    backgroundSize: '100%',
                    ...lazyLoadImageBackground
                  }}
                  data-bg={coloredImgUrl || imgUrl.url}
                />
              </div>
            </div>
            <div className="large-tool-item-text">
              <div className="large-tool-item-header">
                <HeadingText level={2}
                  className={tickerHelper(
                    appTitle,
                    'large-tool-item-heading',
                    25
                  )}
                >
                  {appTitle}
                </HeadingText>
              </div>
              <h4 className="large-tool-item-subheading">{shortDescription}</h4>
            </div>
          </div>
        </div>
      </WrappedLink>
    );
  }

  return (
    <WrappedLink
      className={classNames('undo-link-styles', 'add-focus-visible', {
        'tile-link-wrapper': !isNewLayoutEligible
      })}
      onClick={clickToolHandler()}
      to={`${path}/${to}`}
      ref={ref}
    >
      {isNewLayoutEligible ? (
        <div className="category-tool-tile-new">
          <div className="category-tool-tile-inner">
            <div className="category-tool-tile-media-wrapper">
              <div
                className={classNames(
                  { lazyload: lazyLoadImage },
                  'category-tool-tile-image'
                )}
                style={{
                  background: `center center no-repeat`,
                  backgroundSize: '100%',
                  ...lazyLoadImageBackground
                }}
                data-bg={coloredImgUrl || imgUrl.url}
              />
            </div>
            <div className="category-tool-tile-text-wrapper">
              <div className="category-tool-tile-header">
                <HeadingText level={2}
                  className={tickerHelper(
                    appTitle,
                    'category-tool-tile-heading',
                    25
                  )}
                >
                  {appTitle}
                </HeadingText>
              </div>
              <h4 className="category-tool-tile-subheading">
                {shortDescription}
              </h4>
            </div>
          </div>
          {isCollections && (
            <div className="category-tool-tile-button-wrapper">
              <Button style={{ whiteSpace: 'nowrap' }} size="sm">
                {content.learnMore}
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div className="category-tool-tile">
          <div
            className={classNames('category-tool-tile-context', {
              old: !isColoredIconsEligible
            })}
          >
            <div
              className={classNames(
                { lazyload: lazyLoadImage },
                'category-tool-tile-image',
                { 'partner-marketplace': partnerOrMarketplace }
              )}
              style={{
                background: `center center no-repeat`,
                backgroundSize:
                  !isColoredIconsEligible && !partnerOrMarketplace
                    ? '80%'
                    : '100%',
                ...lazyLoadImageBackground
              }}
              data-bg={coloredImgUrl || imgUrl.url}
            />
            <div className="category-tool-tile-title">
              <h3
                className={tickerHelper(
                  appTitle,
                  partnerOrMarketplace
                    ? 'category-tool-tile-title-spacing'
                    : '',
                  16
                )}
              >
                {appTitle}
              </h3>
            </div>
            {!hideFavorites && (
              <Favorite
                tool={{
                  favorite: toolServFavorites.includes(appKey)
                }}
                toggleFavorite={handleFavoriteClick}
                handleClickTool={clickToolHandler}
                isLoggedIn={get(app, 'fptiData.lgin', true)}
                ariaLabel={ariaLabel}
                title={appTitle}
              />
            )}
          </div>
          <div className="category-tool-tile-text">
            <BodyText >{appBody}</BodyText>
          </div>
        </div>
      )}
    </WrappedLink>
  );
};

CategoryToolTile.propTypes = {
  tool: PropTypes.object,
  collection: PropTypes.object,
  section: PropTypes.object,
  path: PropTypes.string,
  fpti: PropTypes.string,
  fptiImpression: PropTypes.func,
  handleClickTool: PropTypes.func,
  toolTileProps: PropTypes.object,
  insideSection: PropTypes.bool,
  pageLink: PropTypes.string,
  hideFavorites: PropTypes.bool,
  crossSellType: PropTypes.string,
  staffPickType: PropTypes.string,
  intentPickType: PropTypes.string,
  intentRank: PropTypes.number,
  largeTile: PropTypes.bool,
  structuredPSData: PropTypes.object,
  isCollections: PropTypes.bool,
  lazyLoadImage: PropTypes.bool
};

export default CategoryToolTile;
